// Pixel art for the campfire's flame, generated as plain RGBA so it can be baked into one texture
// strip per (style, size) and so a preview script can render exactly what the game draws.
//
// One frame holds everything that BURNS, laid over the cold pit art (packages/tools/src/campfire):
// a bed of coals that flicker on the crossed logs, flame tongues in hard-edged palette bands (the
// LPC look -- no soft blobs), sparks, and for a rain-damaged fire, smoke. The warm light the fire
// throws on the stones and ground is a separate additive halo (fireHalo), because a Graphics or a
// sprite has one blend mode and the flame must stay opaque to read in daylight.
//
// SEAMLESS LOOP: every frame is a pure function of a loop phase u in [0,1). The flame's noise is
// tileable in y and scrolls up exactly one period per loop; sways are whole-number multiples of
// 2*pi*u; sparks and smoke age as frac(u - birth). So frame N wraps to frame 0 with no jump.
//
// SIZE IS A LEVEL, NOT A SCALE: a dying fire draws a lower, narrower flame with fewer sparks, baked
// at one texel per world pixel. Scaling pixel art by 0.6 would drop rows and shimmer as it animates.

export type FireStyle = "campfire" | "sputtering"

export const FIRE_FRAME_W = 40
export const FIRE_FRAME_H = 60
export const FIRE_FRAME_COUNT = 24
export const FIRE_LEVELS = 5
// Where the middle of the coal bed sits inside a frame. FireSprite puts this point on the pit.
export const FIRE_COAL_X = 20
export const FIRE_COAL_Y = 53

export const FIRE_HALO_W = 64
export const FIRE_HALO_H = 44
export const FIRE_HALO_X = 32 // the halo's centre, also seated on the coals
export const FIRE_HALO_Y = 26

// Full-size flame. Height is from the coals to the tallest tongue tips.
const FLAME_MAX_H = 34
const FLAME_HALF_W = 8

type RGB = [number, number, number]
const rgb = (hex: number): RGB => [(hex >> 16) & 0xff, (hex >> 8) & 0xff, hex & 0xff]

interface Palette {
    flame: RGB[] // outer -> core: edge, body, bright, hot, white-hot
    coal: RGB[] // dull -> bright
    spark: RGB[] // young -> old
    halo: RGB // additive: keep it starved of green or it lands on lime over grass
}

const PALETTE: Palette = {
    flame: [0xa8240c, 0xe8501a, 0xff8f1f, 0xffc93c, 0xfff1b0].map(rgb),
    coal: [0x6b1a0c, 0xb8320e, 0xf06a1a, 0xffb43a].map(rgb),
    spark: [0xffe27a, 0xffa02a, 0xd8461a].map(rgb),
    halo: rgb(0xff4a0c),
}

const frac = (n: number): number => n - Math.floor(n)
const mod = (n: number, m: number): number => ((n % m) + m) % m
const TAU = Math.PI * 2

// Deterministic integer hash -> [0,1). Bakes are reproducible (and testable); fires still differ
// because each one starts on a random frame.
const hash = (x: number, y: number, seed: number): number => {
    let h = (x * 374761393 + y * 668265263 + seed * 2147483647) | 0
    h = Math.imul(h ^ (h >>> 13), 1274126177)
    h ^= h >>> 16
    return (h >>> 0) / 4294967296
}

// Value noise whose lattice repeats every `period` cells in y, so scrolling it by exactly one period
// over the loop is seamless.
const loopNoise = (x: number, y: number, period: number, seed: number): number => {
    const x0 = Math.floor(x), y0 = Math.floor(y)
    let tx = x - x0, ty = y - y0
    tx = tx * tx * (3 - 2 * tx)
    ty = ty * ty * (3 - 2 * ty)
    const ya = mod(y0, period), yb = mod(y0 + 1, period)
    const a = hash(x0, ya, seed) * (1 - tx) + hash(x0 + 1, ya, seed) * tx
    const b = hash(x0, yb, seed) * (1 - tx) + hash(x0 + 1, yb, seed) * tx
    return a * (1 - ty) + b * ty
}

const levelFraction = (level: number): number => Math.max(1, Math.min(FIRE_LEVELS, level)) / FIRE_LEVELS

/** The level a strength (0..1, intensity x burn-down) draws at; never 0 while the fire exists. */
export const fireLevel = (strength: number): number =>
    Math.max(1, Math.min(FIRE_LEVELS, Math.ceil(Math.max(0, strength) * FIRE_LEVELS - 1e-6)))

const blend = (out: Uint8Array, i: number, c: RGB, alpha: number) => {
    if (alpha <= 0) return
    const a0 = out[i + 3] / 255
    const a = alpha + a0 * (1 - alpha)
    for (let k = 0; k < 3; k++) {
        out[i + k] = Math.round((c[k] * alpha + out[i + k] * a0 * (1 - alpha)) / a)
    }
    out[i + 3] = Math.round(a * 255)
}

const put = (out: Uint8Array, stride: number, ox: number, x: number, y: number, c: RGB, alpha = 1) => {
    if (x < 0 || x >= FIRE_FRAME_W || y < 0 || y >= FIRE_FRAME_H) return
    blend(out, (y * stride + ox + x) * 4, c, alpha)
}

// The coals: a flat bed on the crossed logs, every coal breathing on its own slow cycle.
const drawCoals = (out: Uint8Array, stride: number, ox: number, u: number, pal: Palette, lf: number) => {
    const rx = 7 + 5 * lf, ry = 2.4 + 1.4 * lf
    for (let y = Math.floor(FIRE_COAL_Y - ry - 1); y <= FIRE_COAL_Y + ry + 1; y++) {
        for (let x = Math.floor(FIRE_COAL_X - rx - 1); x <= FIRE_COAL_X + rx + 1; x++) {
            const dx = (x + 0.5 - FIRE_COAL_X) / rx, dy = (y + 0.5 - FIRE_COAL_Y) / ry
            const d = dx * dx + dy * dy
            if (d > 1) continue
            const seed = hash(x, y, 7)
            if (seed < 0.25 + 0.35 * d) continue // sparse at the rim
            const cycles = 1 + Math.floor(hash(x, y, 8) * 3)
            const breathe = 0.5 + 0.5 * Math.sin(TAU * (u * cycles + hash(x, y, 9)))
            const heat = (1 - d) * 0.75 + breathe * 0.45 + (seed - 0.5) * 0.3
            const c = heat > 0.95 ? pal.coal[3] : heat > 0.65 ? pal.coal[2] : heat > 0.38 ? pal.coal[1] : pal.coal[0]
            put(out, stride, ox, x, y, c)
        }
    }
}

// The flame is a few tongues licking up out of one base, each leaning and pulsing on its own
// whole-number cycle; the tallest stands in the middle. Their union is then carved by rising noise.
const TONGUES = [
    { dx: 0, height: 1, width: 0.8, cycles: 1, phase: 0 },
    { dx: -0.45, height: 0.66, width: 0.55, cycles: 2, phase: 1.7 },
    { dx: 0.5, height: 0.74, width: 0.5, cycles: 3, phase: 3.9 },
    { dx: 0.15, height: 0.86, width: 0.42, cycles: 2, phase: 5.1 },
]

const drawFlame = (
    out: Uint8Array, stride: number, ox: number, u: number, pal: Palette, lf: number, pulse: number,
) => {
    const flameH = FLAME_MAX_H * (0.3 + 0.7 * lf) * pulse
    const halfW = FLAME_HALF_W * (0.55 + 0.45 * lf) * (0.75 + 0.25 * pulse)
    const baseY = FIRE_COAL_Y - 0.5
    const cx = FIRE_COAL_X
    const tongues = TONGUES.map(t => ({ ...t,
        h: flameH * t.height * (0.86 + 0.14 * Math.sin(TAU * t.cycles * u + t.phase)) }))
    for (let y = 0; y < FIRE_FRAME_H; y++) {
        const h = (baseY - (y + 0.5)) / flameH // 0 at the coals, 1 at the tallest tips
        if (h < -0.12 || h > 1.35) continue
        for (let x = 0; x < FIRE_FRAME_W; x++) {
            let body = -1
            for (const t of tongues) {
                const th = (baseY - (y + 0.5)) / t.h
                if (th > 1.2) continue
                // each tongue leans out with height and licks side to side (periodic in u)
                const sway = th * (t.dx * 0.5 + 0.2 * Math.sin(TAU * u + th * 2.4 + t.phase) +
                    0.08 * Math.sin(TAU * 3 * u + th * 5.5 + 1.3 + t.phase))
                const width = th < 0 ? Math.sqrt(Math.max(0, 1 - (th / 0.14) ** 2)) : Math.pow(Math.max(0, 1 - th), 0.75)
                const xn = (x + 0.5 - cx) / halfW - t.dx * Math.min(1, th * 2) - sway
                body = Math.max(body, 1 - Math.abs(xn) / Math.max(width * t.width, 0.04))
            }
            if (body < -0.6) continue
            // Two octaves rising through the flame, one loop period each (seamless). The coarse one
            // carves the tongues, the fine one ragged edges and the bits that break away.
            const n = 0.62 * loopNoise((x - cx) / 3.2 + 40, (y + 0.5) / 6 + u * 12, 12, 1) +
                0.38 * loopNoise((x - cx) / 1.7 + 40, (y + 0.5) / 3 + u * 36, 36, 2)
            const hh = Math.max(0, h)
            const heat = body * 0.95 + (1 - hh) * 0.42 - hh * 0.18 - n * (0.5 + 0.45 * hh) + 0.05
            const band = heat > 0.92 ? 4 : heat > 0.72 ? 3 : heat > 0.5 ? 2 : heat > 0.3 ? 1 : heat > 0.16 ? 0 : -1
            if (band < 0) continue
            put(out, stride, ox, x, y, pal.flame[band])
        }
    }
}

// Sparks spat up out of the fire: each a pixel with a lifetime, drifting and fading. A fuller fire
// throws more and throws them higher.
const drawSparks = (out: Uint8Array, stride: number, ox: number, u: number, pal: Palette, lf: number) => {
    const count = Math.round(2 + 5 * lf)
    for (let i = 0; i < count; i++) {
        const birth = hash(i, 0, 21)
        const lifetimes = 1 + Math.floor(hash(i, 1, 21) * 2) // whole number per loop: seamless
        const age = frac((u - birth) * lifetimes)
        if (age > 0.92) continue
        const rise = (16 + 26 * lf) * (0.7 + 0.5 * hash(i, 2, 21))
        const x = FIRE_COAL_X + (hash(i, 3, 21) - 0.5) * 10 +
            Math.sin(TAU * (age * 1.5 + hash(i, 4, 21))) * 2.5 + (hash(i, 5, 21) - 0.5) * 8 * age
        const y = FIRE_COAL_Y - 6 - rise * age
        const c = age < 0.35 ? pal.spark[0] : age < 0.7 ? pal.spark[1] : pal.spark[2]
        put(out, stride, ox, Math.round(x), Math.round(y), c, age > 0.75 ? 0.6 : 1)
    }
}

const SMOKE: RGB[] = [0x6e6466, 0x8a8082].map(rgb)

// A doused fire smokes: soft grey puffs that swell and thin as they rise.
const drawSmoke = (out: Uint8Array, stride: number, ox: number, u: number) => {
    for (let i = 0; i < 4; i++) {
        const age = frac(u - i / 4)
        const r = 1.5 + age * 4
        const px = FIRE_COAL_X + Math.sin(TAU * (age + i * 0.3)) * 3 + age * 4
        const py = FIRE_COAL_Y - 8 - age * 34
        const alpha = 0.45 * Math.sin(Math.PI * age)
        for (let y = Math.floor(py - r); y <= py + r; y++) {
            for (let x = Math.floor(px - r); x <= px + r; x++) {
                const d = Math.hypot(x + 0.5 - px, y + 0.5 - py) / r
                if (d > 1) continue
                put(out, stride, ox, x, y, SMOKE[d < 0.5 ? 1 : 0], alpha)
            }
        }
    }
}

const drawFrame = (rgba: Uint8Array, stride: number, ox: number, style: FireStyle, level: number, u: number) => {
    const lf = levelFraction(level)
    // Rain-damaged: the flame gutters (a periodic pulse with dead moments) and smokes.
    const pulse = style === "sputtering"
        ? 0.3 + 0.55 * Math.pow(0.5 + 0.5 * Math.sin(TAU * 3 * u + Math.sin(TAU * u)), 2)
        : 1
    if (style === "sputtering") drawSmoke(rgba, stride, ox, u)
    drawCoals(rgba, stride, ox, u, PALETTE, lf)
    drawFlame(rgba, stride, ox, u, PALETTE, lf, pulse)
    drawSparks(rgba, stride, ox, u, PALETTE, style === "sputtering" ? lf * 0.3 : lf)
}

/** One frame (FIRE_FRAME_W x FIRE_FRAME_H RGBA) at loop phase u -- u and u + 1 draw the same frame. */
export const fireFrame = (style: FireStyle, level: number, u: number): Uint8Array => {
    const rgba = new Uint8Array(FIRE_FRAME_W * FIRE_FRAME_H * 4)
    drawFrame(rgba, FIRE_FRAME_W, 0, style, level, u)
    return rgba
}

/**
 * Every frame of one style at one level, side by side in one RGBA strip
 * (FIRE_FRAME_COUNT * FIRE_FRAME_W wide, FIRE_FRAME_H tall).
 */
export const fireStrip = (style: FireStyle, level: number): { rgba: Uint8Array; width: number; height: number } => {
    const width = FIRE_FRAME_W * FIRE_FRAME_COUNT
    const height = FIRE_FRAME_H
    const rgba = new Uint8Array(width * height * 4)
    for (let f = 0; f < FIRE_FRAME_COUNT; f++) {
        drawFrame(rgba, width, f * FIRE_FRAME_W, style, level, f / FIRE_FRAME_COUNT)
    }
    return { rgba, width, height }
}

/**
 * The fire's light on its surroundings, for an ADDITIVE layer: a broad warm pool on the ground
 * (flattened to the ground's perspective) under a tighter glow around the flame. Straight alpha:
 * pixi premultiplies a buffer texture on upload, so ADD lands colour x falloff.
 */
export const fireHalo = (): { rgba: Uint8Array; width: number; height: number } => {
    const width = FIRE_HALO_W, height = FIRE_HALO_H
    const rgba = new Uint8Array(width * height * 4)
    const c = PALETTE.halo
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const gx = (x + 0.5 - FIRE_HALO_X) / 30, gy = (y + 0.5 - FIRE_HALO_Y) / 13
            const ground = Math.max(0, 1 - Math.sqrt(gx * gx + gy * gy))
            const fx = (x + 0.5 - FIRE_HALO_X) / 13, fy = (y + 0.5 - (FIRE_HALO_Y - 12)) / 17
            const flame = Math.max(0, 1 - Math.sqrt(fx * fx + fy * fy))
            const a = Math.min(1, 0.55 * ground * ground + 0.35 * flame * flame)
            const i = (y * width + x) * 4
            rgba[i] = c[0]
            rgba[i + 1] = c[1]
            rgba[i + 2] = c[2]
            rgba[i + 3] = Math.round(a * 255)
        }
    }
    return { rgba, width, height }
}

/** The halo's brightness on a given frame: a loop-periodic flicker so the light breathes with the flame. */
export const fireHaloFlicker = (frame: number): number => {
    const u = frame / FIRE_FRAME_COUNT
    return 0.82 + 0.1 * Math.sin(TAU * 2 * u) + 0.08 * Math.sin(TAU * 5 * u + 1.7)
}
